import { createRouter, createWebHistory } from 'vue-router';

import PartnersApp from '@/components/PartnersApp/PartnersApp.vue';
import type { Capability } from '@/auth/capabilities';
import { refreshAuthSession, useAuth } from '@/auth/useAuth';
import type { RouteRecordRaw, RouterScrollBehavior } from 'vue-router';

declare module 'vue-router' {
  interface RouteMeta {
    auth?: boolean;
    requires?: Capability;
  }
}

const routes: RouteRecordRaw[] = [
  {
    path: '/menu/',
    name: 'QRMenu',
    component: () => import('@/views/QrMenuView/QrMenuView.vue'),
    children: [
      {
        path: ':partnerSlug/',
        name: 'menu-list',
        component: () => import('@/views/QrMenuLocations/QrMenuLocations.vue'),
      },
      {
        path: ':partnerSlug/:partnerLocation/',
        name: 'menu-list-activities',
        component: () => import('@/views/QrMenuListView/QrMenuListView.vue'),
      },
      {
        path: ':partnerSlug/:partnerLocation/:activitySlug/',
        name: 'menu-information',
        component: () =>
          import('@/views/QrMenuInformationView/QrMenuInformationView.vue'),
      },
    ],
  },
  {
    path: '/dev/impersonate',
    name: 'dev-view',
    component: () => import('@/views/DevView/DevView.vue'),
  },
  {
    path: '/',
    name: 'base',
    component: PartnersApp,
    meta: { auth: true },
    redirect: { name: 'home' },
    children: [
      {
        path: '',
        name: 'home',
        component: () => import('@/views/HomeView/HomeView.vue'),
        meta: { auth: true },
      },
      {
        path: 'agenda/',
        name: 'schedule',
        component: () => import('@/views/ScheduleView/ScheduleView.vue'),
        meta: { auth: true, requires: 'booking:read' },
      },
      {
        path: 'liquidaciones/',
        name: 'liquidations',
        component: () =>
          import('@/views/LiquidationsView/LiquidationsView.vue'),
        meta: { auth: true, requires: 'billing:read' },
      },
      {
        path: 'liquidaciones/upload/',
        name: 'uploadLiquidation',
        component: () =>
          import(
            '@/views/LiquidationUploadWrapper/LiquidationUploadWrapper.vue'
          ),
        meta: { auth: true, requires: 'settlement:upload' },
      },
      {
        path: 'facturacion/crear',
        name: 'createLiquidation',
        component: () =>
          import(
            '@/views/ElectronicLiquidationsUploaderView/ElectronicLiquidationsUploaderView.vue'
          ),
        meta: { auth: true, requires: 'settlement:upload' },
      },
      {
        path: 'facturacion/estado/:slug',
        name: 'invoiceUploadStatus',
        component: () =>
          import('@/views/InvoiceUploadStatusView/InvoiceUploadStatusView.vue'),
        meta: { auth: true, requires: 'billing:read' },

        props: true,
      },
      {
        path: 'liquidacion/:id/',
        name: 'liquidation',
        component: () =>
          import(
            '@/views/CompletedLiquidationDetailsView/CompletedLiquidationDetailsView.vue'
          ),
        meta: { auth: true, requires: 'billing:read' },

        props: true,
      },
      {
        path: 'notacredito/:id/',
        name: 'refund',
        component: () =>
          import(
            '@/views/CompletedRefundDetailsView/CompletedRefundDetailsView.vue'
          ),
        meta: { auth: true, requires: 'billing:read' },

        props: true,
      },
      {
        path: 'notacredito/upload/',
        name: 'createRefundARG',
        component: () =>
          import('@/views/RefundUploadARGView/RefundUploadARGView.vue'),
        meta: { auth: true, requires: 'settlement:upload' },

        props: true,
      },
      {
        path: 'configuracion-reservas/',
        name: 'reservation-configuration',
        component: () =>
          import(
            '@/views/ReservationConfigurationView/ReservationConfigurationView.vue'
          ),
        meta: { auth: true, requires: 'reservation:configure' },
      },
      {
        path: 'configuracion-reservas/sucursales/',
        name: 'reservation-locations',
        component: () =>
          import(
            '@/views/ReservationLocationsView/ReservationLocationsView.vue'
          ),
        meta: { auth: true, requires: 'reservation:configure' },
      },
      {
        path: 'mis-experiencias/',
        name: 'my-experiences',
        component: () => import('@/views/ExperiencesView/ExperiencesView.vue'),
        meta: { auth: true, requires: 'experience:read' },
      },
      {
        path: 'mis-experiencias/solicitar-cambios/:id',
        name: 'modify-experience-view',
        component: () =>
          import(
            '@/views/ExperienceModifyRequestView/ExperienceModifyRequestView.vue'
          ),
        meta: { auth: true, requires: 'experience:read' },
      },
      {
        path: 'mis-experiencias/activar/:id',
        name: 'activate-experience-view',
        component: () =>
          import(
            '@/views/ExperienceModifyRequestView/ExperienceModifyRequestView.vue'
          ),
        props: { mode: 'activate' },
        meta: { auth: true },
      },
      {
        path: 'mis-experiencias/solicitud-de-alta',
        name: 'new-activity-request',
        component: () =>
          import('@/views/NewActivityRequest/NewActivityRequestView.vue'),
        meta: { auth: true, requires: 'experience:create' },
      },
      {
        path: 'mis-experiencias/solicitud-de-alta/:proposalId/editar',
        name: 'resubmit-new-activity-request',
        component: () =>
          import('@/views/NewActivityRequest/NewActivityRequestView.vue'),
        props: route => ({
          proposalId: route.params.proposalId,
          mode: 'resubmit',
        }),
        meta: { auth: true, requires: 'experience:create' },
      },
      {
        path: 'mi-contenido/',
        name: 'social-media-covers',
        component: () =>
          import('@/views/SocialMediaCovers/SocialMediaCovers.vue'),
        meta: { auth: true, requires: 'experience:read' },
      },
      {
        path: `${encodeURIComponent('reseñas')}/`,
        name: 'reviews',
        component: () => import('@/views/ReviewsView/ReviewsView.vue'),
        meta: { auth: true, requires: 'review:read' },
      },
      {
        path: 'acceso-denegado/',
        name: 'access-denied',
        component: () =>
          import('@/views/AccessDeniedView/AccessDeniedView.vue'),
        meta: { auth: true },
      },
      {
        path: 'perfil/',
        name: 'profile',
        component: () => import('@/views/ProfileView/ProfileView.vue'),
        meta: { auth: true },
      },
      {
        path: 'gestionar-usuarios/',
        name: 'users-management',
        component: () =>
          import('@/views/UsersManagementView/UsersManagementView.vue'),
        meta: { auth: true, requires: 'users:manage' },
      },
      // {
      //   path: 'centro-de-ayuda/',
      //   name: 'help-center',
      //   component: () => import('@/views/HelpCenterView/HelpCenterView.vue'),
      //   meta: { auth: true },
      // },

      // {
      //   path: 'centro-de-ayuda/:categorySlug',
      //   name: 'help-center-faq',
      //   component: () =>
      //     import('@/views/HelpCenterFaqView/HelpCenterFaqView.vue'),
      //   meta: { auth: true },
      // },
      {
        path: 'metricas/',
        name: 'charts',
        component: () => import('@/views/MetricsView/MetricsView.vue'),
        meta: { auth: true, requires: 'metrics:read' },
      },
      {
        path: 'beneficios/',
        name: 'benefits',
        component: () => import('@/views/BenefitsView/BenefitsView.vue'),
        meta: { auth: true, requires: 'benefit:read' },
      },
      {
        path: 'ofertas/',
        name: 'deals',
        component: () => import('@/views/DealsView/DealsView.vue'),
        meta: { auth: true, requires: 'deal:read' },
      },
      {
        path: 'ofertas/nueva/',
        name: 'createDeal',
        component: () => import('@/views/CreateDealView/CreateDealView.vue'),
        meta: { auth: true, requires: 'deal:create' },
      },
      {
        path: 'inicio/',
        redirect: { name: 'home' },
        meta: { auth: true },
      },
    ],
  },
  {
    path: '/login',
    name: 'login',
    component: () =>
      import(
        /*webpackChunkName: "login"*/ '@/views/BaseLoginView/BaseLoginView.vue'
      ),
  },
  {
    path: '/restablecer-contrasena/:hash',
    name: 'restore-password',
    component: () =>
      import('@/views/RestorePasswordView/RestorePasswordView.vue'),
  },
  {
    path: '/invitacion/:token',
    name: 'invitation-accept',
    component: () =>
      import('@/views/InvitationAcceptView/InvitationAcceptView.vue'),
    props: true,
  },
  {
    path: '/pagina-no-encontrada/',
    name: 'page-not-found',
    component: () => import('@/views/PageNotFoundView/PageNotFoundView.vue'),
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    redirect: { name: 'page-not-found', params: {} },
  },
];

const scrollBehavior: RouterScrollBehavior = (to, from, savedPosition) => {
  if (savedPosition) {
    return savedPosition;
  }
  return { top: 0, left: 0 };
};

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior,
});

router.beforeEach(to => {
  if (!to.meta.auth) return true;

  refreshAuthSession();
  const { canAccessRoute, isReady } = useAuth();

  if (!isReady.value) return { name: 'login' };
  if (!canAccessRoute(to.matched)) {
    return { name: 'access-denied' };
  }
  return true;
});
